import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/motion";
import { EnquiryForm } from "@/components/EnquiryForm";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";

export const Contact = () => {
    const { t, tHtml, site: siteData } = useLang();
    const { SITE } = siteData;
    const CARDS = [
        {
            icon: Mail,
            label: t("contact.emailLabel", "Email"),
            value: SITE.emailDisplay,
            testid: "contact-card-email",
            href: null,
        },
        {
            icon: Phone,
            label: t("contact.phoneLabel", "Phone"),
            value: SITE.phoneDisplay,
            testid: "contact-card-phone",
            href: null,
        },
        {
            icon: MapPin,
            label: t("contact.officeLabel", "Office"),
            value: SITE.officeDisplay,
            testid: "contact-card-office",
            href: null,
        },
    ];
    usePageMeta(
        t("meta.contact.title", "Contact"),
        t("meta.contact.desc", "Contact UniversHeal — reach our healthcare coordination team for international patients in Kerala, India, or send a general enquiry.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("contact.eyebrow", "Contact")}
                title={t("contact.title", "Talk to us.")}
                intro={t("contact.intro", "Questions about your situation, our services or healthcare in Kerala? Send us a message — a person, not a bot, will read it.")}
            />

            <section className="pb-8">
                <div className="container-site">
                    <div className="grid sm:grid-cols-3 gap-5">
                        {CARDS.map((card, i) => (
                            <Reveal key={card.label} delay={i * 0.07} className="h-full">
                                <div
                                    data-testid={card.testid}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-7"
                                >
                                    <card.icon
                                        className="w-5 h-5 text-forest"
                                        strokeWidth={1.5}
                                    />
                                    <p className="eyebrow text-moss mt-5">
                                        {card.label}
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-charcoal/85">
                                        {card.value}
                                    </p>
                                    {card.href && (
                                        <a
                                            href={card.href}
                                            className="mt-3 inline-block text-sm font-semibold text-pine underline underline-offset-4"
                                        >
                                            {card.testid === "contact-card-email"
                                                ? t("contact.writeToUs", "Write to us")
                                                : t("contact.callUs", "Call us")}
                                        </a>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.1}>
                        <a
                            href={SITE.whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp"
                            className="mt-5 inline-flex btn-primary"
                        >
                            <MessageCircle className="w-4 h-4" />
                            {t("common.chat_whatsapp", "Chat on WhatsApp")}
                        </a>
                        <p className="mt-3 text-xs text-moss">
                            {t("contact.whatsappNote", "[WhatsApp link — placeholder, to be replaced with the official business number before launch.]")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.coordinator}
                                alt="An Indian patient-care coordinator warmly welcoming an international patient at a premium modern hospital reception"
                                data-testid="contact-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-8">
                <div className="container-site max-w-3xl">
                    <Reveal>
                        <EnquiryForm
                            service="General Enquiry"
                            submitLabel={t("form.sendMessage", "Send Message")}
                            testIdPrefix="contact-form"
                        />
                    </Reveal>
                </div>
            </section>
        </>
    );
};
